// client/src/components/auth/Signin.js
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useState, useRef, useCallback, useMemo } from 'react'
import ApiAuth from '../../api/ApiAuth'
import toastr from 'toastr'
import './Auth.css'
import { track } from '../../analytics'
import useAutofillSync from '../../hooks/useAutofillSync'
import Footer from '../common/Footer'

function getSafeRedirectPath(next) {
  if (!next || !next.startsWith('/') || next.startsWith('//')) {
    return '/'
  }

  return next
}

function Signin() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loginData, setloginData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  })
  const [loading, setLoading] = useState(false)
  const [generalError, setGeneralError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Refs for autofill detection
  const emailRef = useRef(null)
  const passwordRef = useRef(null)

  // Sync autofilled values to state
  const handleAutofillSync = useCallback((values) => {
    setloginData((prev) => ({ ...prev, ...values }))
  }, [])

  // Memoize refs object to prevent infinite loop
  const autofillRefs = useMemo(
    () => ({ email: emailRef, password: passwordRef }),
    [],
  )

  useAutofillSync(autofillRefs, handleAutofillSync)

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setloginData((prevloginData) => ({
      ...prevloginData,
      [name]: type === 'checkbox' ? checked : value,
    }))
    // Clear error when user starts typing
    if (generalError) {
      setGeneralError('')
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (loading) return

    // Clear previous error
    setGeneralError('')

    setLoading(true)
    try {
      const result = await ApiAuth.login(loginData)

      if (result.success) {
        // Track successful sign-in
        try {
          track('user_signed_in', {
            email: loginData.email,
            method: 'email',
            timestamp: new Date().toISOString(),
          })
        } catch (analyticsErr) {
          console.error('Analytics tracking error:', analyticsErr)
        }

        // Force a full page reload to remount AuthProvider with new JWT cookie
        // This avoids infinite loops from calling refreshUser during navigation
        const next = searchParams.get('next')
        window.location.href = getSafeRedirectPath(next)
      } else if (result.requiresVerification) {
        // Track email verification required
        try {
          track('user_signin_failed', {
            method: 'email',
            reason: 'email_not_verified',
            timestamp: new Date().toISOString(),
          })
        } catch (analyticsErr) {
          console.error('Analytics tracking error:', analyticsErr)
        }

        // Show specific error message for unverified email
        const errorMsg =
          result.message ||
          'Please verify your email address before logging in. Check your inbox for the verification link.'
        setGeneralError(errorMsg)
        toastr.error(errorMsg, { timeOut: 8000 })

        // Redirect to verification pending page after a short delay
        setTimeout(() => {
          navigate('/verify-email-pending')
        }, 3000)
      } else {
        // Track failed sign-in attempt (invalid credentials)
        try {
          track('user_signin_failed', {
            method: 'email',
            reason: 'invalid_credentials',
            timestamp: new Date().toISOString(),
          })
        } catch (analyticsErr) {
          console.error('Analytics tracking error:', analyticsErr)
        }

        // Use generic message to prevent user enumeration attacks
        const errorMsg = 'Invalid credentials. Please try again.'
        setGeneralError(errorMsg)
        toastr.error(errorMsg)
      }
    } catch (error) {
      console.error('Login error:', error)

      // Track failed sign-in attempt (error/exception)
      try {
        track('user_signin_failed', {
          method: 'email',
          reason: 'error',
          errorType: error?.name || 'unknown',
          timestamp: new Date().toISOString(),
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }

      const errorMsg = 'An error occurred during login. Please try again.'
      setGeneralError(errorMsg)
      toastr.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">Serve Sync</div>
          <h1 className="auth-title">Welcome Back</h1>

          <form
            onSubmit={handleSubmit}
            className="auth-form"
            aria-describedby={generalError ? 'form-error' : undefined}
          >
            {generalError && (
              <div
                id="form-error"
                className="form-error-message"
                data-testid="signin-general-error"
                style={{ marginBottom: '1rem' }}
                role="alert"
                aria-live="polite"
              >
                ⚠️ {generalError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                ref={emailRef}
                value={loginData.email}
                onChange={handleInputChange}
                name="email"
                type="email"
                className="form-input"
                data-testid="signin-email-input"
                id="email"
                placeholder="Enter your email"
                required
                disabled={loading}
                aria-invalid={generalError ? 'true' : 'false'}
                aria-describedby="email-help"
              />
              <div id="email-help" className="form-help-text">
                We'll never share your email with anyone else.
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="password-input-wrapper">
                <input
                  ref={passwordRef}
                  value={loginData.password}
                  onChange={handleInputChange}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  data-testid="signin-password-input"
                  id="password"
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  aria-invalid={generalError ? 'true' : 'false'}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <label className="form-group checkbox-group" htmlFor="rememberMe">
              <input
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                checked={loginData.rememberMe}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="form-label-inline">Remember me for 7 days</span>
            </label>

            <button
              type="submit"
              className={`auth-submit-btn ${loading ? 'loading' : ''}`}
              data-testid="signin-submit-button"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="auth-links">
            <button
              className="auth-link-btn"
              onClick={() => navigate('/sign-up')}
              disabled={loading}
            >
              Don't have an account? Create one →
            </button>
            <Link to="/forgot-password" className="auth-link-btn">
              Forgot your password?
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

export default Signin
